// src/oura/ask/AskOura.tsx
//
// Ask Oura (PREVIEW): a staff-only chat for signed clients that sits on top
// of the screens Oura already has. Three jobs: it guides staff on Oura (how
// do I, where is, what does this mean), answers from the client's own
// records, and prepares key work by sending staff to the existing screen
// that does it (a Delivery drawer, the Page workshop, Create a doc on the
// Client tab). It never writes, sends, queues or spends: there is no fetch
// or POST anywhere in this module.
//
// Mounted once in OuraShell for staff on /oura/businesses and every page
// under it: "All clients" on the list, the client's name inside one client.
// Opened from the Ask bar at the top of each of those pages, the client
// sidebar and ⌘J.
//
// Look: a chat in the manner of claude.ai. A 640px white sheet; a new chat is
// a greeting and four starter cards; staff questions are grey bubbles on the
// right; Oura's answers are plain prose on white with numbered citations, a
// tidy table, steps or list only where it helps, then quiet actions and
// follow-ups.

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ClipboardEvent, type DragEvent, type FormEvent, type KeyboardEvent, type MouseEvent, type ReactNode } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { ArrowUpIcon, ArrowUpRightIcon, CheckIcon, ChevronRightIcon, CopyIcon, FilePlus2Icon, ImagePlusIcon, RotateCcwIcon, SquareIcon, SquarePenIcon, XIcon } from "lucide-react";
import { useOuraRole } from "../role";
import { OURA_ROOT } from "../nav";
import { getBusiness } from "../data";
import { cn } from "../lib/cn";
import { FOCUS_RING, useEscapeLayer } from "../ui";
import { allAnswer, answerText, clientAnswer, isDrafting, noticeFor, startersFor, type AskStarter } from "./answers";
import { historyOf, liveAnswer, type LiveAsk } from "./live";
import { ASK_AI_ON, ASK_IMAGES_ON, runOuraAction, type AskAiTurn } from "@service/aeo/ask";
import { ASK_IMAGES_ACCEPT, ASK_IMAGES_MAX, takeImageFiles, toAskImage } from "./images";
import {
    askOura,
    askPending,
    askStop,
    claimStream,
    closeAsk,
    currentStream,
    newChat,
    patchAnswer,
    fillAnswersWith,
    postQuestion,
    releaseStream,
    releaseThreadStream,
    setAskOpen,
    settle,
    settledAt,
    toggleAsk,
    useAskOpen,
    usePendingFor,
    useStopAsked,
    useStreaming,
    useThread,
    ASK_KEY_HINT,
} from "./store";
import { useAllData, useClientData } from "./useAskData";
import { GreetingMascot, Mascot, OuraFace } from "./Mascot";
import {
    scopeKey,
    type AskAnswer,
    type AskBlock,
    type AskCtx,
    type AskGo,
    type AskImage,
    type AskMessage,
    type AskRow,
    type AskSource,
    type AskTone,
    type AskTopicGroup,
    type NextStep,
    type ProposalPreview,
    type TriageGroup,
} from "./types";

/* ------------------------------------------------------------------------- *
 * Shared look
 * ------------------------------------------------------------------------- */

const INK = "text-[#111827]";
const INK2 = "text-[#6B7280]";
/** 15/26: the chat body. */
const CHAT = "text-[15px] leading-[26px]";
/** 14/22: tables, lists, cards. */
const BODY = "text-sm leading-[22px]";
const META = "text-[13px] leading-[18px]";
const MICRO = "text-xs leading-4";

/** One card style for every action card, here and on Delivery. */
export const ASK_CARD = "rounded-2xl bg-white p-4 ring-1 ring-black/[0.08]";
/** The same card with no padding, for cards whose rows run edge to edge. */
const ASK_FRAME = "rounded-2xl bg-white ring-1 ring-black/[0.08]";
const BTN = cn("inline-flex h-9 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-4 text-[13px] font-medium transition-colors", FOCUS_RING);
const BTN_PRIMARY = cn(BTN, "bg-blue-600 text-white hover:bg-blue-700");
const BTN_SECONDARY = cn(BTN, "bg-white text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50");
/** A chip that asks a question: follow-ups and the How do I menu. */
const CHIP = cn(
    META,
    "inline-flex min-h-8 items-center rounded-full bg-white px-3 py-1 text-left text-gray-700 ring-1 ring-inset ring-black/[0.1] transition-colors hover:bg-gray-50 hover:text-gray-900",
    FOCUS_RING,
);

/** The motion this module uses, defined once. Sheet: Apple's sheet curve, no overshoot at the screen edge. */
const KEYFRAMES = `
@keyframes askSheetIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
@keyframes askFade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
@keyframes askShimmer { from { background-position: 100% 0; } to { background-position: -100% 0; } }
@keyframes askPop { from { opacity: 0; transform: translateY(-2px) scale(0.98); } to { opacity: 1; transform: none; } }
@keyframes askReading { 0%, 100% { opacity: 0.35; transform: scale(0.82); } 50% { opacity: 0.9; transform: scale(1.08); } }
`;
const SHEET_IN = "animate-[askSheetIn_420ms_cubic-bezier(0.32,0.72,0,1)] motion-reduce:animate-none";
/** The panel's width: 460px below 1440px wide, so the page stays readable beside it; 640px from 1440px. */
const PANEL_W = "min-[720px]:w-[460px] min-[1440px]:w-[640px] min-[720px]:max-w-[94vw]";
const FADE_IN = "animate-[askFade_260ms_cubic-bezier(0.2,0.8,0.2,1)_both] motion-reduce:animate-none";

/** Card header: a grey label stacked above the title, both left. */
export function CardHead({ label, title }: { label: string; title: string }) {
    return (
        <div>
            <p className={cn(MICRO, "m-0", INK2)}>{label}</p>
            <p className={cn("m-0 mt-0.5 text-[15px] font-semibold leading-[22px]", INK)}>{title}</p>
        </div>
    );
}

function useReducedMotion(): boolean {
    const [reduce, setReduce] = useState(() => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);
    useEffect(() => {
        const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
        if (!mq) return;
        const on = () => setReduce(mq.matches);
        mq.addEventListener("change", on);
        return () => mq.removeEventListener("change", on);
    }, []);
    return reduce;
}

/* ------------------------------------------------------------------------- *
 * Mount point and the page's Ask bar
 * ------------------------------------------------------------------------- */

/** Staff pages the chat lives on: the Businesses list and every client page under it. */
const onAskPage = (path: string) => path === `${OURA_ROOT}/businesses` || path.startsWith(`${OURA_ROOT}/businesses/`);

export function AskOuraMount({ businessId }: { businessId?: string | null }) {
    const role = useOuraRole();
    const { pathname } = useLocation();
    const open = useAskOpen();
    const live = role === "oura-staff" && onAskPage(pathname);
    // ⌘J (Ctrl+J elsewhere) opens and closes the chat on any staff Oura page.
    // Esc closes it through the panel's own escape layer.
    useEffect(() => {
        if (!live) return;
        const onKey = (e: globalThis.KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === "j") {
                e.preventDefault();
                toggleAsk();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [live]);
    if (!live || !open) return null;
    return businessId ? <ClientPanel key={`client:${businessId}`} businessId={businessId} /> : <AllPanel />;
}

/**
 * The Ask bar at the top of a staff page: an input that looks like the
 * composer. Typing and Enter opens the chat and asks; Enter on an empty bar,
 * or the keycap, just opens it.
 */
export function AskOuraBar({ className }: { className?: string }) {
    const { businessId } = useParams();
    const role = useOuraRole();
    const [text, setText] = useState("");
    if (role !== "oura-staff") return null;
    const key = businessId ? scopeKey({ kind: "client", businessId }) : scopeKey({ kind: "all" });
    const name = businessId ? (getBusiness(businessId)?.name ?? "this client") : null;
    const placeholder = name ? `Ask Oura about ${name}` : "Ask Oura about your clients";
    const submit = (e: FormEvent) => {
        e.preventDefault();
        askOura(key, text);
        setText("");
    };
    return (
        <form onSubmit={submit} role="search" aria-label="Ask Oura" className={cn("w-full max-w-[720px]", className)} data-ask-bar>
            <div className="group relative">
                <OuraFace size={20} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                    type="text"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Escape") (e.target as HTMLInputElement).blur();
                    }}
                    aria-label={placeholder}
                    autoComplete="off"
                    className={cn(
                        "block h-11 w-full rounded-2xl border-0 bg-white pl-11 pr-16 text-sm text-[#111827] ring-1 ring-inset ring-black/[0.08] transition-shadow",
                        "shadow-[0_1px_2px_rgba(0,0,0,0.04),0_4px_12px_-6px_rgba(0,0,0,0.08)] hover:ring-black/[0.14] focus:outline-none focus:ring-2 focus:ring-blue-600",
                    )}
                />
                {/* The placeholder as real text, so it reads (and is found) like the page's own copy. */}
                {!text && (
                    <span aria-hidden className="pointer-events-none absolute left-11 right-16 top-1/2 -translate-y-1/2 truncate text-sm text-gray-500" data-ask-bar-placeholder>
                        {placeholder}
                    </span>
                )}
                <button
                    type="button"
                    onClick={() => askOura(key, text)}
                    aria-label="Open Ask Oura"
                    aria-keyshortcuts="Meta+J Control+J"
                    className={cn(
                        "absolute right-2.5 top-1/2 inline-flex h-6 -translate-y-1/2 items-center rounded-md bg-gray-50 px-1.5 text-xs font-medium text-gray-500 ring-1 ring-inset ring-gray-200 hover:text-gray-900",
                        FOCUS_RING,
                    )}
                >
                    {ASK_KEY_HINT}
                </button>
            </div>
        </form>
    );
}

/* ------------------------------------------------------------------------- *
 * The two scopes
 * ------------------------------------------------------------------------- */

function ClientPanel({ businessId }: { businessId: string }) {
    const data = useClientData(businessId);
    // The cross-client reads too, so "What needs me today?" here is the same
    // list as on Businesses, filtered to this client.
    const all = useAllData();
    // Never the id: until the records name the business, it is "this client" (an UUID showed, 29 Oct 2026).
    const known = getBusiness(businessId)?.name ?? (data.name && data.name !== businessId ? data.name : null);
    const name = known ?? "this client";
    const build = useCallback((q: string, ctx: AskCtx) => clientAnswer(q, data, all, ctx), [data, all]);
    // The server-backed answers (an action preview, the change log, Oura's AI step), each behind its own switch.
    const live = useCallback((q: string, sync: AskAnswer | null, history: AskAiTurn[], images?: AskImage[]) => liveAnswer(q, { kind: "client", businessId }, data, name, history, sync, images), [businessId, data, name]);
    const agreedNotice = data.ready ? noticeFor(data) : null;
    // A failed read is answered at once ("Couldn't load", with a retry); so is a failed all-clients read once this client's records are in.
    const answerable = data.error || (data.ready && (all.ready || all.error));
    const retry = useCallback(() => {
        if (data.error) data.retry();
        if (all.error) all.retry();
    }, [data, all]);
    return (
        <Panel
            scope={scopeKey({ kind: "client", businessId })}
            scopeLabel={name}
            greeting={`What do you want to know about ${name}?`}
            reading={`Reading ${name}'s records`}
            placeholder={`Ask Oura anything about ${name}`}
            footnote={`Preview · answers from ${name}'s records in Oura. Nothing is sent or changed.`}
            starters={startersFor({ kind: "client", businessId }, data.ready ? data : null)}
            notice={agreedNotice ? { text: agreedNotice, action: "Start drafts", prompt: "Start the page drafts", mood: "love" } : null}
            ready={answerable}
            retry={retry}
            build={build}
            live={live}
        />
    );
}

function AllPanel() {
    const data = useAllData();
    const build = useCallback((q: string, ctx: AskCtx) => allAnswer(q, data, ctx), [data]);
    const live = useCallback((q: string, sync: AskAnswer | null, history: AskAiTurn[], images?: AskImage[]) => liveAnswer(q, { kind: "all" }, null, "your clients", history, sync, images), []);
    return (
        <Panel
            scope={scopeKey({ kind: "all" })}
            scopeLabel="All clients"
            greeting="What do you want to know about your clients?"
            reading="Reading every client's records"
            placeholder="Ask Oura anything about your clients"
            footnote="Preview · answers from every client's records in Oura. Nothing is sent or changed."
            starters={startersFor({ kind: "all" })}
            notice={null}
            ready={data.ready || data.error}
            retry={data.retry}
            build={build}
            live={live}
        />
    );
}

/* ------------------------------------------------------------------------- *
 * The panel
 * ------------------------------------------------------------------------- */

const LINE = 24;
const MAX_LINES = 6;
/** A server call that threw with nothing to fall back on (never a hanging question). */
const NO_ANSWER: AskAnswer = { blocks: [{ kind: "p", sentences: [{ text: "Oura did not answer just now. Ask again in a moment." }] }], followUps: [] };

interface PanelProps {
    scope: string;
    scopeLabel: string;
    greeting: string;
    reading: string;
    placeholder: string;
    footnote: string;
    starters: AskStarter[];
    /** A line under a new chat, and the moment Oura's face plays on it (Love: the client approved). */
    notice: { text: string; action: string; prompt: string; mood: "love" | "surprised" } | null;
    /** The records are in, or a read failed (then the answer says so). */
    ready: boolean;
    /** Read the failed records again. */
    retry: () => void;
    build: (question: string, ctx: AskCtx) => AskAnswer | null;
    /** A server-backed answer for the question, or null when the matching's own answer stands (live.ts). */
    live: (question: string, sync: AskAnswer | null, history: AskAiTurn[], images?: AskImage[]) => LiveAsk | null;
}

function Panel({ scope, scopeLabel, greeting, reading, placeholder, footnote, starters, notice, ready, retry, build: buildWith, live }: PanelProps) {
    const { messages } = useThread(scope);
    const streaming = useStreaming();
    const hasPending = usePendingFor(scope);
    const [draft, setDraft] = useState("");
    // Pictures picked for the next question (behind VITE_ENABLE_OURA_ASK_IMAGES; the AI step reads them).
    const picturesOn = ASK_IMAGES_ON && ASK_AI_ON;
    const [pictures, setPictures] = useState<AskImage[]>([]);
    const [pictureNote, setPictureNote] = useState<string | null>(null);
    const [over, setOver] = useState(false);
    const fileInput = useRef<HTMLInputElement>(null);
    // One conversion at a time; a send waits for it, and a scope change drops a late result.
    const [converting, setConverting] = useState(false);
    const conversionBusy = useRef(false);
    const scopeGeneration = useRef(0);
    useEffect(() => {
        scopeGeneration.current += 1;
        conversionBusy.current = false;
        setConverting(false);
        setPictures([]);
        setPictureNote(null);
        setOver(false);
    }, [scope]);
    const addFiles = useCallback(
        async (incoming: File[]) => {
            if (!picturesOn || incoming.length === 0) return;
            if (conversionBusy.current) {
                setPictureNote("Wait for the current picture to finish loading.");
                return;
            }
            const { files, error } = takeImageFiles(pictures.length, incoming);
            setPictureNote(error);
            if (files.length === 0) return;
            const generation = scopeGeneration.current;
            const base = pictures.length;
            conversionBusy.current = true;
            setConverting(true);
            try {
                // One bad file does not drop the good ones picked with it.
                const settled = await Promise.allSettled(files.map((f, i) => toAskImage(f, base + i)));
                if (generation !== scopeGeneration.current) return;
                const made = settled.flatMap((r) => (r.status === "fulfilled" ? [r.value] : []));
                setPictures((cur) => [...cur, ...made].slice(0, ASK_IMAGES_MAX));
                if (made.length !== files.length) {
                    setPictureNote(made.length ? "Some pictures could not be read. Try another PNG, JPEG or WebP file." : "That picture could not be read. Try another PNG, JPEG or WebP file.");
                }
            } finally {
                if (generation === scopeGeneration.current) {
                    conversionBusy.current = false;
                    setConverting(false);
                }
            }
        },
        [picturesOn, pictures.length],
    );
    const onPasteInput = (e: ClipboardEvent<HTMLTextAreaElement>) => {
        const files = Array.from(e.clipboardData?.files ?? []);
        if (!picturesOn || files.length === 0) return;
        e.preventDefault();
        void addFiles(files);
    };
    const dropHandlers = picturesOn
        ? {
              onDragOver: (e: DragEvent<HTMLElement>) => {
                  if (!Array.from(e.dataTransfer.types).includes("Files")) return;
                  e.preventDefault();
                  setOver(true);
              },
              onDragLeave: () => setOver(false),
              onDrop: (e: DragEvent<HTMLElement>) => {
                  setOver(false);
                  const files = Array.from(e.dataTransfer.files ?? []);
                  if (files.length === 0) return;
                  e.preventDefault();
                  addFiles(files);
              },
          }
        : {};
    const list = useRef<HTMLDivElement>(null);
    const inner = useRef<HTMLDivElement>(null);
    const input = useRef<HTMLTextAreaElement>(null);
    const pinned = useRef(true);
    /** The message to keep at the top of the view while its answer arrives, until staff scroll themselves. */
    const anchor = useRef<string | null>(null);
    const seen = useRef(messages.length);
    // Esc and the X close as staff asked: focus goes back to what opened the chat.
    const close = closeAsk;
    useEscapeLayer(close, true);

    // The full list of what Ask Oura does is shown at most once per conversation.
    const menuShown = messages.some((m) => m.answer?.blocks.some((b) => b.kind === "topics"));
    const build = useCallback((q: string) => buildWith(q, { menuShown }), [buildWith, menuShown]);

    // One answer at a time: while one reveals, a send does nothing (the store refuses it, at the
    // moment of the click, so two quick clicks never post two questions).
    // Every question is posted with its answer open and filled by `fill` below (the next effect pass):
    // the matching's own answer, or a server call (an action preview, the change log, Oura's AI step)
    // that fills when it returns. One path for the composer, the page's Ask bar and a retry.
    const send = useCallback(
        (text: string, images?: AskImage[]) => {
            // A picture with no words asks the obvious question.
            const t = text.trim() || (images && images.length ? "What do you see in this picture?" : "");
            if (!t || currentStream()) return;
            pinned.current = true;
            postQuestion(scope, t, null, images);
        },
        [scope],
    );
    // Try again on a "Couldn't load" answer: read the failed records again, then ask the same
    // question with its answer left open, so it is filled once the new reads land.
    const retryAsk = useCallback(
        (question: string) => {
            if (currentStream()) return;
            retry();
            pinned.current = true;
            postQuestion(scope, question, null);
        },
        [retry, scope],
    );
    // This chat going away (closed, or another client) hands back a stream one of its answers holds.
    useEffect(() => () => releaseThreadStream(scope), [scope]);

    // Answers waiting on the records are filled once they have landed: the matching's own answer,
    // or a server call whose slot stays open (with what the chat is doing) until it returns. A failed
    // call shows the matching's own answer, so no question is left hanging.
    const history = useRef(messages);
    history.current = messages;
    const fill = useCallback(
        (q: string, images?: AskImage[]) => {
            const sync = build(q);
            if (!sync) return null;
            return live(q, sync, historyOf(history.current), images) ?? sync;
        },
        [build, live],
    );
    useEffect(() => {
        if (ready) fillAnswersWith(scope, fill, NO_ANSWER);
    }, [ready, fill, scope, messages.length]);
    // A question typed in the page's Ask bar, asked once no answer is revealing. The store checks the
    // stream at this moment, not this render's value: an answer shown again can take it in the same
    // effect pass. Until it can be posted here with its answer it stays pending, and the stream going
    // free or the records landing (`ready`) retries.
    // Before paint, and with its answer left open while the records are read: a question typed in the
    // page's Ask bar opens the chat on that question and "Reading…", never on the greeting first.
    useLayoutEffect(() => {
        if (!hasPending) return;
        if (askPending(scope, (q) => (ready ? build(q) : null), { unanswered: true })) pinned.current = true;
    }, [hasPending, streaming, scope, ready, build]);

    useEffect(() => {
        input.current?.focus();
    }, []);
    // A new question (or a message Oura posts by itself): keep it at the top of the view, so its answer is
    // read from its first line as it arrives, not from the bottom. Reopening the chat lands at the bottom.
    useLayoutEffect(() => {
        const grew = messages.length > seen.current;
        seen.current = messages.length;
        if (!grew) return;
        const last = messages[messages.length - 1];
        const prev = messages[messages.length - 2];
        anchor.current = last?.from === "oura" && prev?.from === "staff" ? prev.id : (last?.id ?? null);
    }, [messages]);
    useLayoutEffect(() => {
        const el = list.current;
        const body = inner.current;
        if (!el || !body) return;
        const stick = () => {
            const at = anchor.current ? body.querySelector<HTMLElement>(`[data-ask-id="${anchor.current}"]`) : null;
            if (at) {
                const top = el.scrollTop + at.getBoundingClientRect().top - el.getBoundingClientRect().top - 12;
                el.scrollTop = Math.max(0, Math.min(top, el.scrollHeight - el.clientHeight));
            } else if (pinned.current) el.scrollTop = el.scrollHeight;
        };
        stick();
        const ro = new ResizeObserver(stick);
        ro.observe(body);
        // Staff scrolling by hand ends the hold.
        const release = () => {
            anchor.current = null;
        };
        el.addEventListener("wheel", release, { passive: true });
        el.addEventListener("touchmove", release, { passive: true });
        el.addEventListener("keydown", release);
        return () => {
            ro.disconnect();
            el.removeEventListener("wheel", release);
            el.removeEventListener("touchmove", release);
            el.removeEventListener("keydown", release);
        };
    }, [messages.length]);
    // Copying from an answer leaves out the source chips, so a [1] never lands inside a number.
    const onCopy = (e: ClipboardEvent<HTMLDivElement>) => {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
        const frag = sel.getRangeAt(0).cloneContents();
        if (!frag.querySelector("[data-ask-cite]")) return;
        frag.querySelectorAll("[data-ask-cite]").forEach((x) => x.remove());
        const box = document.createElement("div");
        box.appendChild(frag);
        e.clipboardData.setData("text/plain", (box.textContent ?? "").replace(/[ \t]+\n/g, "\n").replace(/ {2,}/g, " "));
        e.preventDefault();
    };
    // Grow the composer with its text, up to six lines.
    useLayoutEffect(() => {
        const el = input.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${Math.min(el.scrollHeight, LINE * MAX_LINES + 32)}px`;
    }, [draft]);

    const sendDraft = () => {
        if (streaming || converting) return;
        send(draft, pictures);
        setDraft("");
        setPictures([]);
        setPictureNote(null);
    };
    const submit = (e: FormEvent) => {
        e.preventDefault();
        sendDraft();
    };
    const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            sendDraft();
        } else if (e.key === "ArrowUp" && draft === "") {
            const last = [...messages].reverse().find((m) => m.from === "staff");
            if (last) {
                e.preventDefault();
                setDraft(last.text);
            }
        }
    };

    const hasText = draft.trim().length > 0 || pictures.length > 0;
    const lastOura = [...messages].reverse().find((m) => m.from === "oura")?.id;
    // A question on its way from the Ask bar is not an empty chat: no greeting flashes before it.
    const empty = messages.length === 0 && !hasPending;

    return (
        <aside
            role="complementary"
            aria-label={`Ask Oura, ${scopeLabel}`}
            data-ask-oura-panel
            className={cn(
                "fixed inset-y-0 right-0 z-30 flex w-full flex-col border-l border-black/[0.08] bg-white",
                PANEL_W,
                "shadow-[-24px_0_64px_-24px_rgba(17,24,39,0.22)]",
                SHEET_IN,
            )}
        >
            <style>{KEYFRAMES}</style>
            <header className="flex h-14 shrink-0 items-center gap-2.5 px-4">
                <OuraFace size={20} wink />
                <h2 className={cn("m-0 text-sm font-semibold", INK)}>Ask Oura</h2>
                <span className={cn(META, "min-w-0 truncate rounded-full bg-gray-100 px-2.5 py-0.5", INK2)} data-ask-scope>
                    {scopeLabel}
                </span>
                <span className="flex-1" />
                <IconButton
                    label="New chat"
                    onClick={() => {
                        newChat(scope);
                        setDraft("");
                        input.current?.focus();
                    }}
                    disabled={empty}
                >
                    <SquarePenIcon aria-hidden size={17} strokeWidth={1.75} />
                </IconButton>
                <IconButton label="Close Ask Oura" onClick={close}>
                    <XIcon aria-hidden size={18} strokeWidth={1.75} />
                </IconButton>
            </header>

            <div
                ref={list}
                data-ask-scroll
                onScroll={(e) => {
                    const el = e.currentTarget;
                    pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
                }}
                onCopy={onCopy}
                className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden"
            >
                <div ref={inner} className={cn("mx-auto w-full max-w-[600px] px-5 sm:px-6", empty ? "flex min-h-full flex-col justify-center py-8" : "pb-8 pt-4")}>
                    {empty ? (
                        <EmptyState greeting={greeting} starters={starters} notice={notice} onSend={send} typing={draft.trim().length > 0} />
                    ) : (
                        <div className="space-y-7" aria-live="polite">
                            {messages.map((m) =>
                                m.from === "staff" ? (
                                    <StaffMessage key={m.id} id={m.id} text={m.text} images={m.images} />
                                ) : (
                                    <Answer key={m.id} message={m} latest={m.id === lastOura} reading={m.waiting ?? reading} onSend={send} onRetry={retryAsk} onPatch={(a) => patchAnswer(scope, m.id, a)} />
                                ),
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className="shrink-0 pb-3 pt-1">
                <form onSubmit={submit} className="mx-auto w-full max-w-[600px] px-5 sm:px-6">
                    <div
                        {...dropHandlers}
                        className={cn(
                            "relative rounded-3xl bg-white shadow-[0_1px_2px_rgba(0,0,0,0.05),0_6px_20px_-10px_rgba(0,0,0,0.12)] ring-1 transition-shadow focus-within:ring-black/[0.2]",
                            over ? "ring-2 ring-blue-500" : "ring-black/[0.1]",
                        )}
                    >
                        {pictures.length > 0 && (
                            <ul className="m-0 flex list-none flex-wrap gap-2 px-4 pt-3" data-ask-picked={pictures.length}>
                                {pictures.map((img, i) => (
                                    <li key={img.name + i} className="relative h-16 w-20 shrink-0 overflow-hidden rounded-lg bg-gray-100 ring-1 ring-black/[0.06]" title={img.name}>
                                        <img src={img.url} alt={img.name} className="h-full w-full object-cover" />
                                        <button
                                            type="button"
                                            onClick={() => setPictures((cur) => cur.filter((_, j) => j !== i))}
                                            aria-label={`Remove ${img.name}`}
                                            className={cn("absolute right-1 top-1 grid h-5 w-5 place-items-center rounded-full bg-gray-900/70 text-white hover:bg-gray-900", FOCUS_RING)}
                                        >
                                            <XIcon aria-hidden size={11} strokeWidth={2.5} />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                        <label className="sr-only" htmlFor="ask-oura-input">
                            Ask Oura
                        </label>
                        <textarea
                            id="ask-oura-input"
                            ref={input}
                            rows={1}
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={onKey}
                            onPaste={onPasteInput}
                            placeholder={pictures.length > 0 ? "Ask about the picture, or just send it" : placeholder}
                            autoComplete="off"
                            className={cn(
                                "block min-h-[56px] w-full resize-none rounded-3xl border-0 bg-transparent py-4 placeholder:text-gray-400 focus:outline-none focus:ring-0",
                                picturesOn ? "pl-12 pr-14" : "pl-5 pr-14",
                                "text-[15px] leading-6",
                                INK,
                            )}
                        />
                        {picturesOn && (
                            <>
                                <input
                                    ref={fileInput}
                                    type="file"
                                    accept={ASK_IMAGES_ACCEPT}
                                    multiple
                                    className="hidden"
                                    onChange={(e) => {
                                        void addFiles(Array.from(e.target.files ?? []));
                                        e.target.value = "";
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={() => fileInput.current?.click()}
                                    disabled={!!streaming || converting || pictures.length >= ASK_IMAGES_MAX}
                                    aria-label="Add a picture"
                                    title="Add a picture (or paste one)"
                                    className={cn("absolute bottom-3 left-3 inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:opacity-40", FOCUS_RING)}
                                    data-ask-add-picture
                                >
                                    <ImagePlusIcon aria-hidden size={18} strokeWidth={1.75} />
                                </button>
                            </>
                        )}
                        {streaming ? (
                            <button
                                type="button"
                                aria-label="Stop"
                                onClick={() => askStop(streaming)}
                                className={cn("absolute bottom-3 right-3 inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#111827] text-white hover:bg-black", FOCUS_RING)}
                                data-ask-stop
                            >
                                <SquareIcon aria-hidden size={11} fill="currentColor" strokeWidth={0} />
                            </button>
                        ) : (
                            <button
                                type="submit"
                                aria-label="Send"
                                disabled={!hasText || converting}
                                className={cn(
                                    "absolute bottom-3 right-3 inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors",
                                    hasText && !converting ? "bg-[#111827] text-white hover:bg-black" : "cursor-default bg-gray-100 text-gray-400",
                                    FOCUS_RING,
                                )}
                            >
                                <ArrowUpIcon aria-hidden size={16} strokeWidth={2.25} />
                            </button>
                        )}
                    </div>
                    <p className={cn(MICRO, "m-0 mt-2 text-center", pictureNote ? "text-amber-700" : "text-gray-400")} role={pictureNote ? "alert" : undefined}>
                        {pictureNote ?? footnote}
                    </p>
                </form>
            </div>
        </aside>
    );
}

function IconButton({ label, onClick, disabled, children }: { label: string; onClick: () => void; disabled?: boolean; children: ReactNode }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-label={label}
            title={label}
            className={cn(
                "inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:cursor-default disabled:text-gray-300 disabled:hover:bg-transparent",
                FOCUS_RING,
            )}
        >
            {children}
        </button>
    );
}

/* ------------------------------------------------------------------------- *
 * A new chat
 * ------------------------------------------------------------------------- */

/**
 * A new chat: Oura springs in and idles (the one calm loop); it looks up while staff type. When the client has
 * approved answers waiting, Oura's face on that line plays Love once, just after the greeting lands.
 */
function EmptyState({ greeting, starters, notice, onSend, typing }: { greeting: string; starters: AskStarter[]; notice: PanelProps["notice"]; onSend: (t: string) => void; typing: boolean }) {
    return (
        <div className={cn("flex flex-col items-center", FADE_IN)} data-ask-empty>
            <GreetingMascot size={72} typing={typing} />
            <h3 className={cn("m-0 mt-4 max-w-[460px] text-balance text-center text-[28px] font-semibold leading-[34px] tracking-[-0.02em]", INK)}>{greeting}</h3>
            <div className="mt-8 grid w-full grid-cols-1 gap-3 min-[520px]:grid-cols-2" data-ask-starters>
                {starters.map((st) => (
                    <button
                        key={st.prompt}
                        type="button"
                        onClick={() => onSend(st.prompt)}
                        className={cn(
                            "group rounded-2xl bg-white p-4 text-left ring-1 ring-black/[0.06] transition-[transform,box-shadow] duration-200 ease-out",
                            "hover:-translate-y-0.5 hover:shadow-[0_8px_24px_-12px_rgba(17,24,39,0.18)] hover:ring-black/[0.1] motion-reduce:hover:translate-y-0",
                            FOCUS_RING,
                        )}
                        data-ask-starter={st.prompt}
                    >
                        <span className={cn("block text-sm font-medium leading-5", INK)}>{st.prompt}</span>
                        <span className={cn(META, "mt-1 block truncate", INK2)}>{st.hint}</span>
                    </button>
                ))}
            </div>
            {notice && (
                <div className="mt-3 flex w-full items-center gap-3 rounded-2xl bg-[#F9FAFB] px-4 py-3" data-ask-notice>
                    <OuraFace size={16} react={notice.mood} delay={700} />
                    <p className={cn(META, "m-0 min-w-0 flex-1", INK2)}>
                        <span className={cn("font-medium", INK)}>Oura noticed</span> · {notice.text}
                    </p>
                    <button type="button" onClick={() => onSend(notice.prompt)} className={cn(META, "shrink-0 rounded font-medium text-blue-600 hover:text-blue-700", FOCUS_RING)}>
                        {notice.action}
                    </button>
                </div>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------------- *
 * Messages
 * ------------------------------------------------------------------------- */

function StaffMessage({ id, text, images }: { id: string; text: string; images?: AskImage[] }) {
    return (
        <div className={cn("flex flex-col items-end gap-1.5", FADE_IN)} data-ask-message="staff" data-ask-id={id}>
            {images && images.length > 0 && (
                <ul className="m-0 flex max-w-[80%] list-none flex-wrap justify-end gap-1.5 p-0" data-ask-pictures={images.length}>
                    {images.map((img) => (
                        <li key={img.name + img.data.length} className="overflow-hidden rounded-xl ring-1 ring-black/[0.08]">
                            <img src={img.url} alt={img.name} className="block h-24 w-auto max-w-[220px] object-cover" />
                        </li>
                    ))}
                </ul>
            )}
            <p className={cn(CHAT, "m-0 max-w-[80%] whitespace-pre-wrap break-words rounded-3xl bg-[#F3F4F6] px-4 py-2.5", INK)}>{text}</p>
        </div>
    );
}

/** A link that opens a drawer, a sheet or a card on its screen closes the chat, so what it opened is in view. */
const opensDrawer = (to: string) => /[?&](d|doc|request|q|page|issue|create|compose|filter|tab|view|engine|rival)=|[?&]from=plan\b/.test(to);
const follow = (to: string) => (e: MouseEvent) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey) return;
    if (opensDrawer(to)) setAskOpen(false);
};

const sourceKey = (src: AskSource) => `${src.label}\u0000${src.to}`;

/** Every source an answer cites, numbered in the order they first appear. */
function numberSources(answer: AskAnswer): Map<string, number> {
    const out = new Map<string, number>();
    const add = (src?: AskSource) => {
        if (src && !out.has(sourceKey(src))) out.set(sourceKey(src), out.size + 1);
    };
    for (const b of answer.blocks) {
        if (b.kind === "p") b.sentences.forEach((x) => x.cites?.forEach(add));
        else if (b.kind === "table") b.rows.forEach((r) => add(r.cite));
    }
    return out;
}

const words = (t: string) => t.split(/\s+/).filter(Boolean);
/** What a non-prose block costs in the reveal, in words. */
const BLOCK_COST = 6;
const blockCost = (b: AskBlock) => (b.kind === "p" ? b.sentences.reduce((sum, x) => sum + words(x.text).length, 0) : BLOCK_COST);

const TICK_MS = 25;
/** Total reveal stays under this, however long the answer. */
const MAX_REVEAL_MS = 1400;

type Phase = "thinking" | "streaming" | "done";

function Answer({ message, latest, reading, onSend, onRetry, onPatch }: { message: AskMessage; latest: boolean; reading: string; onSend: (t: string) => void; onRetry: (question: string) => void; onPatch: (a: AskAnswer) => void }) {
    const answer = message.answer ?? null;
    const reduce = useReducedMotion();
    const already = settledAt(message.id);
    const [phase, setPhase] = useState<Phase>(already !== undefined ? "done" : "thinking");
    const [shown, setShown] = useState(0);
    const [cut, setCut] = useState<number | null>(already ?? null);
    const [fresh] = useState(already === undefined);
    const stopAsked = useStopAsked(message.id);
    // The one stream: this answer moves past Thinking only while it holds it.
    const holder = useStreaming();
    const mine = holder === message.id;
    const born = useRef(Date.now());
    const delay = useRef(500 + Math.round(Math.random() * 400));

    const costs = useMemo(() => (answer ? answer.blocks.map(blockCost) : []), [answer]);
    const total = costs.reduce((a, b) => a + b, 0);

    // Take the stream when it is free (an answer shown again, or one Oura posted by itself).
    useEffect(() => {
        if (phase !== "done" && holder === null) claimStream(message.id);
    }, [phase, holder, message.id]);
    // Unmounted before it finished (the chat closed, the client changed): give the stream back,
    // so the next chat is never left with a Stop for an answer no one can see.
    useEffect(() => () => releaseStream(message.id), [message.id]);

    // Thinking until it holds the stream, the records are read and a short beat has passed.
    useEffect(() => {
        if (phase !== "thinking" || !mine) return;
        if (!answer) return;
        if (reduce) {
            setPhase("done");
            settle(message.id, null);
            return;
        }
        const wait = Math.max(0, delay.current - (Date.now() - born.current));
        const t = window.setTimeout(() => setPhase("streaming"), wait);
        return () => window.clearTimeout(t);
    }, [phase, answer, reduce, message.id, mine]);

    // Reveal word by word, quickly.
    useEffect(() => {
        if (phase !== "streaming") return;
        const step = Math.max(1, Math.ceil(total / (MAX_REVEAL_MS / TICK_MS)));
        const t = window.setInterval(() => setShown((x) => Math.min(total, x + step)), TICK_MS);
        return () => window.clearInterval(t);
    }, [phase, total]);
    useEffect(() => {
        if (phase === "streaming" && shown >= total) {
            setPhase("done");
            settle(message.id, null);
        }
    }, [phase, shown, total, message.id]);

    // Stop: keep what is on screen, drop the rest.
    useEffect(() => {
        if (!stopAsked || phase === "done") return;
        const at = phase === "thinking" ? 0 : shown;
        setCut(at);
        setPhase("done");
        settle(message.id, at);
    }, [stopAsked, phase, shown, message.id]);

    const numbers = useMemo(() => (answer ? numberSources(answer) : new Map<string, number>()), [answer]);

    if (phase === "thinking" || !answer) {
        if (phase === "done") return <Stopped />;
        return (
            <div data-ask-message="oura" data-ask-phase="thinking" data-ask-id={message.id}>
                {isDrafting(message.text, answer) ? <Thinking reading={reading.replace(/^Reading\b/, "Drafting from")} mood="evolving" /> : <Thinking reading={reading} mood="thinking" />}
            </div>
        );
    }

    const budget = phase === "done" ? (cut ?? total) : shown;
    let start = 0;
    const visible: ReactNode[] = [];
    answer.blocks.forEach((b, i) => {
        const left = budget - start;
        start += costs[i];
        if (left <= 0) return;
        if (b.kind === "p") {
            visible.push(<Prose key={i} block={b} budget={left} numbers={numbers} />);
        } else if (left >= BLOCK_COST) {
            visible.push(
                <div key={i} className={fresh ? FADE_IN : undefined}>
                    <Block block={b} numbers={numbers} onSend={onSend} onRetry={() => onRetry(message.text)} onPatch={onPatch} latest={latest} />
                </div>,
            );
        }
    });
    const finished = phase === "done";

    return (
        <div className="group/answer relative" data-ask-message="oura" data-ask-phase={phase} data-ask-id={message.id}>
            <OuraFace size={20} react={fresh && latest && foundLate(answer) ? "surprised" : null} className="absolute left-0 top-[3px]" />
            <div className="space-y-4 pl-8">{visible}</div>
            {finished && cut !== null && (
                <div className="pl-8">
                    <Stopped inline />
                </div>
            )}
            {/* Copy and the follow-ups act on the whole answer, so they exist only once it ran to the end. */}
            {finished && cut === null && (
                <AnswerFoot answer={answer} question={message.text} latest={latest} onSend={onSend} followUps={latest ? answer.followUps : []} fresh={fresh} />
            )}
        </div>
    );
}

/** Something late or come back in this answer: Oura's face plays Surprised once as it lands. */
const foundLate = (a: AskAnswer) => a.blocks.some((b) => b.kind === "triage" && b.groups.some((g) => g.key === "late" && g.rows.length > 0));

/**
 * While Oura works: Thinking as it reads, Evolving as it drafts. Oura's helmet sits where the answer's face
 * will, opening on its reading eyes, with a soft blue glow behind it that breathes while it reads.
 */
function Thinking({ reading, mood }: { reading: string; mood: "thinking" | "evolving" }) {
    return (
        <div className="flex items-center gap-2" data-ask-thinking={mood} role="status">
            <span className="relative -my-[7px] -ml-[6px] -mr-0.5 inline-flex h-8 w-8 shrink-0 translate-y-[2px]" data-ask-avatar="reading">
                <span
                    aria-hidden
                    className="absolute left-[3px] top-[1px] h-[27px] w-[27px] rounded-full bg-blue-600/[0.14] ring-1 ring-blue-600/25 animate-[askReading_1.4s_ease-in-out_infinite] motion-reduce:animate-none motion-reduce:opacity-50"
                />
                <Mascot mood={mood} size={32} className="relative" />
            </span>
            <span
                className={cn(
                    META,
                    "bg-[linear-gradient(90deg,#9CA3AF_0%,#9CA3AF_35%,#111827_50%,#9CA3AF_65%,#9CA3AF_100%)] bg-[length:200%_100%] bg-clip-text text-transparent",
                    "animate-[askShimmer_1.6s_linear_infinite] motion-reduce:animate-none motion-reduce:text-gray-500",
                )}
            >
                {reading}
            </span>
        </div>
    );
}

function Stopped({ inline }: { inline?: boolean }) {
    return <p className={cn(META, "m-0", inline ? "mt-2" : "", INK2)}>Stopped</p>;
}

function Prose({ block, budget, numbers }: { block: Extract<AskBlock, { kind: "p" }>; budget: number; numbers: Map<string, number> }) {
    let left = budget;
    const out: ReactNode[] = [];
    block.sentences.forEach((x, i) => {
        if (left <= 0) return;
        const w = words(x.text);
        const whole = left >= w.length;
        const text = whole ? x.text : w.slice(0, left).join(" ");
        left -= w.length;
        const chips =
            whole && x.cites?.length
                ? x.cites.map((c) => {
                      const num = numbers.get(sourceKey(c));
                      return num ? <Cite key={sourceKey(c)} n={num} source={c} /> : null;
                  })
                : null;
        // The last word and its citations never break apart, so a [1] never starts a line.
        const cut = chips ? text.lastIndexOf(" ") + 1 : text.length;
        out.push(
            <span key={i}>
                {i > 0 && " "}
                {text.slice(0, cut)}
                {chips && (
                    <span className="whitespace-nowrap">
                        {text.slice(cut)}
                        {chips}
                    </span>
                )}
            </span>,
        );
    });
    return <p className={cn(CHAT, "m-0 max-w-[64ch]", INK)}>{out}</p>;
}

/** [1]: a numbered source. Hover or focus shows the screen it comes from and a link to open it. */
function Cite({ n, source }: { n: number; source: AskSource }) {
    const [open, setOpen] = useState(false);
    const [alignRight, setAlignRight] = useState(false);
    const ref = useRef<HTMLSpanElement>(null);
    const timer = useRef<number | undefined>(undefined);
    const show = () => {
        window.clearTimeout(timer.current);
        const el = ref.current;
        const box = el?.closest("[data-ask-scroll]")?.getBoundingClientRect();
        const me = el?.getBoundingClientRect();
        if (box && me) setAlignRight(me.left + 232 > box.right - 12);
        setOpen(true);
    };
    const hide = () => {
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => setOpen(false), 140);
    };
    useEffect(() => () => window.clearTimeout(timer.current), []);
    return (
        // A small raised pill after the sentence's full stop, with space on both sides, so it never reads as a
        // digit of the number before it. It is left out of any copy (select-none, and the panel's copy handler).
        <span
            ref={ref}
            className="relative top-[-0.5em] ml-[5px] mr-px inline-block select-none align-baseline leading-none"
            onMouseEnter={show}
            onMouseLeave={hide}
            onFocus={show}
            onBlur={hide}
            data-ask-cite
        >
            <button
                type="button"
                aria-label={`Source ${n}: ${source.label}`}
                aria-expanded={open}
                onClick={show}
                className={cn(
                    "inline-flex h-[15px] min-w-[15px] items-center justify-center rounded-full bg-gray-100 px-[4px] text-[10px] font-semibold leading-none tabular-nums text-gray-500 transition-colors hover:bg-blue-50 hover:text-blue-700",
                    open && "bg-blue-50 text-blue-700",
                    FOCUS_RING,
                )}
            >
                {n}
            </button>
            {open && (
                <span
                    role="tooltip"
                    className={cn(
                        "absolute top-full z-10 block w-56 pt-1.5 animate-[askPop_140ms_ease-out] motion-reduce:animate-none",
                        alignRight ? "right-0" : "left-0",
                    )}
                    data-ask-cite-pop
                >
                    <span className="flex items-center gap-3 rounded-xl bg-white px-3 py-2.5 shadow-[0_10px_30px_-10px_rgba(17,24,39,0.25)] ring-1 ring-black/[0.08]">
                        <span className="min-w-0 flex-1">
                            <span className={cn(MICRO, "block", INK2)}>Source</span>
                            <span className={cn(META, "block truncate font-medium", INK)}>{source.label}</span>
                        </span>
                        <Link
                            to={source.to}
                            onClick={follow(source.to)}
                            className={cn(META, "inline-flex shrink-0 items-center gap-0.5 rounded font-medium text-blue-600 hover:text-blue-700", FOCUS_RING)}
                            data-ask-source={source.label}
                        >
                            Open
                            <ArrowUpRightIcon aria-hidden size={13} />
                        </Link>
                    </span>
                </span>
            )}
        </span>
    );
}

function AnswerFoot({
    answer,
    question,
    latest,
    followUps,
    fresh,
    onSend,
}: {
    answer: AskAnswer;
    question: string;
    latest: boolean;
    followUps: string[];
    fresh: boolean;
    onSend: (t: string) => void;
}) {
    const [copied, setCopied] = useState(false);
    const copy = () => {
        void navigator.clipboard?.writeText(answerText(answer)).then(
            () => {
                setCopied(true);
                window.setTimeout(() => setCopied(false), 1500);
            },
            () => undefined,
        );
    };
    return (
        <div className={cn("pl-8", fresh && FADE_IN)}>
            <div
                className={cn(
                    "-ml-1.5 mt-2 flex items-center gap-0.5 transition-opacity",
                    latest ? "opacity-100" : "opacity-0 focus-within:opacity-100 group-hover/answer:opacity-100",
                )}
                data-ask-actions
            >
                <FootButton label={copied ? "Copied" : "Copy"} onClick={copy}>
                    {copied ? <CheckIcon aria-hidden size={14} /> : <CopyIcon aria-hidden size={14} />}
                </FootButton>
                <FootButton label="Ask again" onClick={() => onSend(question)}>
                    <RotateCcwIcon aria-hidden size={14} />
                </FootButton>
                {copied && <span className={cn(MICRO, "ml-1", INK2)}>Copied</span>}
            </div>
            {followUps.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2" data-ask-followups>
                    {followUps.map((f) => (
                        <button
                            key={f}
                            type="button"
                            onClick={() => onSend(f)}
                            className={cn(
                                META,
                                "inline-flex h-8 items-center whitespace-nowrap rounded-full bg-white px-3 text-gray-700 ring-1 ring-inset ring-black/[0.1] transition-colors hover:bg-gray-50 hover:text-gray-900",
                                FOCUS_RING,
                            )}
                        >
                            {f}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function FootButton({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={label}
            title={label}
            className={cn("inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700", FOCUS_RING)}
        >
            {children}
        </button>
    );
}

/* ------------------------------------------------------------------------- *
 * Blocks after the prose
 * ------------------------------------------------------------------------- */

/** One accent: ours is blue, the client's and the rest are grey. No amber, red or green labels. */
const DOT: Record<AskTone, string> = { amber: "bg-gray-400", blue: "bg-blue-600", gray: "bg-gray-400" };
/** Late reads in the ink's own weight, never in red. */
const LATE = "font-medium text-[#111827]";

function Block({ block, numbers, onSend, onRetry, onPatch, latest }: { block: AskBlock; numbers: Map<string, number>; onSend: (t: string) => void; onRetry: () => void; onPatch: (a: AskAnswer) => void; latest: boolean }) {
    switch (block.kind) {
        case "p":
            return null;
        case "table":
            return (
                <table className="w-full max-w-[64ch] border-collapse" data-ask-table>
                    <tbody>
                        {block.rows.map((r, i) => {
                            const num = r.cite ? numbers.get(sourceKey(r.cite)) : undefined;
                            return (
                                <tr key={i} className="border-b border-black/[0.06] first:border-t">
                                    <td className={cn(BODY, "w-[40%] py-2 pr-4 align-top", INK2)}>{r.label}</td>
                                    <td className={cn(BODY, "py-2 align-top", r.late ? LATE : INK)}>
                                        {r.value}
                                        {num && r.cite && <Cite n={num} source={r.cite} />}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            );
        case "list":
            return <ListBlock title={block.title} tone={block.tone} rows={block.rows} more={block.more} />;
        case "steps":
            return <StepsBlock steps={block.steps} go={block.go} />;
        case "go":
            return <GoLink go={block.go} />;
        case "topics":
            return <TopicsBlock groups={block.groups} onSend={onSend} />;
        case "suggest":
            return <SuggestCard block={block} />;
        case "start":
            return <StartCard block={block} />;
        case "triage":
            return <TriageBlock groups={block.groups} />;
        case "proposals":
            return <ProposalsBlock origin={block.origin} items={block.items} />;
        case "next":
            return <NextBlock steps={block.steps} note={block.note} celebrate={latest} />;
        case "links":
            return <LinksBlock links={block.links} />;
        case "choices":
            return <ChoicesBlock items={block.items} onSend={onSend} />;
        case "retry":
            return (
                <button type="button" onClick={onRetry} className={cn(BTN_SECONDARY, "h-9")} data-ask-retry>
                    {block.label}
                </button>
            );
        case "action":
            return <ActionCard block={block} onPatch={onPatch} />;
    }
}

/**
 * A chat action (ourteam-backend #6801): the server's words on what it would change, Do it and Not now.
 * Do it runs it once and replaces the card with what was done and where it shows; a refusal keeps the
 * card with the server's reason. Not now replaces it with one line: nothing changed.
 */
const newActionKey = (): string => (typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

function ActionCard({ block, onPatch }: { block: Extract<AskBlock, { kind: "action" }>; onPatch: (a: AskAnswer) => void }) {
    const [running, setRunning] = useState(false);
    // A synchronous lock: two presses before React commits `running` must not send /run twice.
    const runningRef = useRef(false);
    // One key per card: a server that reads Idempotency-Key replays the first result instead of running it again.
    const keyRef = useRef(newActionKey());
    const [refused, setRefused] = useState<string | null>(null);
    const doIt = async () => {
        if (runningRef.current) return;
        runningRef.current = true;
        setRunning(true);
        setRefused(null);
        let got: Awaited<ReturnType<typeof runOuraAction>>;
        try {
            got = await runOuraAction(block.businessId, block.name, block.params, keyRef.current);
        } finally {
            runningRef.current = false;
            setRunning(false);
        }
        if (!got.ok) {
            if (got.unknown) {
                // No reply: it may have run. Replace the card so nobody presses Do it again blind.
                const delivery = `${OURA_ROOT}/businesses/${encodeURIComponent(block.businessId)}/delivery`;
                onPatch({
                    blocks: [
                        { kind: "p", sentences: [{ text: `${got.message} It may still have run: ${block.says}.`, cites: [{ label: "Delivery", to: delivery }] }, { text: "Check Delivery before asking for it again." }] },
                        { kind: "go", go: { label: "Open Delivery", to: delivery } },
                    ],
                    followUps: ["What should I do next?"],
                });
                return;
            }
            // The server replied and refused: a retry is a new attempt, not a replay of this one.
            keyRef.current = newActionKey();
            setRefused(got.message);
            return;
        }
        const to = got.link ?? `${OURA_ROOT}/businesses/${encodeURIComponent(block.businessId)}/delivery`;
        const where = /\/reviews\b/.test(to) ? "Client tab" : /\/documents\b/.test(to) ? "Documents" : "Delivery";
        const undo = block.undo ? (/^nothing/i.test(block.undo) ? `${block.undo}.` : `To undo: ${block.undo.charAt(0).toLowerCase()}${block.undo.slice(1)}.`) : null;
        // A drafted note comes back as text: shown whole, to read before it is sent from the Client tab.
        const draft = typeof got.result === "string" && got.result.trim() ? got.result.trim() : null;
        onPatch({
            blocks: [
                { kind: "p", sentences: [{ text: `Done: ${got.says}.`, cites: [{ label: where, to }] }, ...(undo ? [{ text: undo }] : [])] },
                ...(draft ? [{ kind: "p", sentences: draft.split(/\n{2,}/).map((x) => ({ text: x.replace(/\s+/g, " ") })) } as AskBlock] : []),
                { kind: "go", go: { label: `Open ${where}`, to } },
            ],
            followUps: ["What should I do next?", "What needs me today?"],
        });
    };
    const notNow = () => {
        if (runningRef.current) return; // the card cannot be replaced while /run is in flight
        onPatch({ blocks: [{ kind: "p", sentences: [{ text: `Not done: ${block.says}. Nothing was changed.` }] }], followUps: ["What should I do next?"] });
    };
    return (
        <div className={cn(ASK_CARD, "space-y-3")} data-ask-card="action" data-ask-action={block.name}>
            <div className="flex items-start gap-3">
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                    <CheckIcon aria-hidden size={17} strokeWidth={1.75} />
                </span>
                <div className="min-w-0 flex-1">
                    <CardHead label={`${block.label} · needs your yes`} title={block.says} />
                    <p className={cn(META, "m-0 mt-1.5", INK2)}>{block.undo ? `Reversible. ${block.undo}.` : "Reversible."}</p>
                    {refused && (
                        <p className={cn(META, "m-0 mt-1.5 text-amber-700")} role="alert">
                            {refused} Nothing was changed.
                        </p>
                    )}
                </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 pl-12">
                <button type="button" onClick={doIt} disabled={running} className={cn(BTN_PRIMARY, running && "opacity-60")} data-ask-do-it>
                    {running ? "Doing it…" : "Do it"}
                </button>
                <button type="button" onClick={notNow} disabled={running} className={BTN_SECONDARY} data-ask-not-now>
                    Not now
                </button>
            </div>
        </div>
    );
}

/** "Did you mean…": the closest questions, each a button that asks it. */
function ChoicesBlock({ items, onSend }: { items: string[]; onSend: (t: string) => void }) {
    return (
        <div className="flex flex-col items-start gap-2" data-ask-choices>
            {items.map((q) => (
                <button key={q} type="button" onClick={() => onSend(q)} className={cn(BTN_SECONDARY, "h-auto min-h-9 whitespace-normal py-1.5 text-left")} data-ask-choice={q}>
                    {q}
                </button>
            ))}
        </div>
    );
}

/** A how-to: the steps, numbered, then the link that opens the screen they start on. */
function StepsBlock({ steps, go }: { steps: string[]; go: AskGo }) {
    return (
        <div className="space-y-3" data-ask-steps>
            <ol className="list-plain m-0 max-w-[64ch] space-y-2.5 p-0">
                {steps.map((x, i) => (
                    <li key={i} className="flex items-start gap-3">
                        <span aria-hidden className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-medium tabular-nums text-gray-600">
                            {i + 1}
                        </span>
                        <span className={cn(BODY, "min-w-0 flex-1", INK)}>
                            <span className="sr-only">Step {i + 1}: </span>
                            {x}
                        </span>
                    </li>
                ))}
            </ol>
            <GoLink go={go} />
        </div>
    );
}

/** The link into the exact screen, drawer or card an answer is about. */
function GoLink({ go }: { go: AskGo }) {
    return (
        <Link to={go.to} onClick={follow(go.to)} className={cn(BTN_SECONDARY, "h-8 px-3.5")} data-ask-go={go.label}>
            {go.label}
            <ArrowUpRightIcon aria-hidden size={14} className="text-gray-500" />
        </Link>
    );
}

/** The questions Ask Oura answers, grouped: each chip asks it. */
function TopicsBlock({ groups, onSend }: { groups: AskTopicGroup[]; onSend: (t: string) => void }) {
    return (
        <div className="space-y-4" data-ask-topics>
            {groups.map((g) => (
                <div key={g.title}>
                    <p className={cn(META, "m-0 mb-2 font-medium", INK2)}>{g.title}</p>
                    <div className="flex flex-wrap gap-2">
                        {g.items.map((q) => (
                            <button key={q} type="button" onClick={() => onSend(q)} className={CHIP} data-ask-topic={q}>
                                {q}
                            </button>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

const ROWS_SHOWN = 5;

function ListBlock({ title, tone, rows, more }: { title: string; tone: AskTone; rows: AskRow[]; more?: string }) {
    const [all, setAll] = useState(false);
    const shown = all ? rows : rows.slice(0, ROWS_SHOWN);
    return (
        <div data-ask-list>
            <p className={cn(META, "m-0 mb-1.5 flex items-center gap-2", INK2)}>
                <span aria-hidden className={cn("h-1.5 w-1.5 rounded-full", DOT[tone])} />
                {title}
                <span className="tabular-nums text-gray-400">{rows.length}</span>
            </p>
            <ul className="list-plain m-0 border-t border-black/[0.06] p-0">
                {shown.map((r, i) => (
                    <li key={i} className="border-b border-black/[0.06]">
                        <Link to={r.to} onClick={follow(r.to)} className={cn("group -mx-2 flex items-start gap-3 rounded-lg px-2 py-2.5 hover:bg-[#F9FAFB]", FOCUS_RING)} data-ask-row>
                            <span className="min-w-0 flex-1">
                                <span className={cn(BODY, "block font-medium", INK)}>{r.title}</span>
                                {r.sub && <span className={cn(META, "mt-0.5 line-clamp-2 block", INK2)}>{r.sub}</span>}
                            </span>
                            {r.meta && <span className={cn(META, "mt-0.5 max-w-[40%] shrink-0 text-right", r.late ? LATE : INK2)}>{r.meta}</span>}
                            <ChevronRightIcon aria-hidden size={14} className="mt-1 shrink-0 text-gray-300 group-hover:text-gray-500" />
                        </Link>
                    </li>
                ))}
            </ul>
            {(rows.length > ROWS_SHOWN || more) && (
                <div className="mt-2 flex items-center gap-3">
                    {rows.length > ROWS_SHOWN && (
                        <button type="button" onClick={() => setAll((v) => !v)} className={cn(META, "rounded font-medium text-blue-600 hover:text-blue-700", FOCUS_RING)}>
                            {all ? "Show fewer" : `Show ${rows.length - ROWS_SHOWN} more`}
                        </button>
                    )}
                    {more && <span className={cn(META, INK2)}>{more}</span>}
                </div>
            )}
        </div>
    );
}

/** The calm line a card shows after a preview button. */
function Confirm({ children }: { children: ReactNode }) {
    return (
        <p role="status" className={cn(META, "m-0 flex items-center gap-2", INK2, FADE_IN)}>
            <CheckIcon aria-hidden size={14} className="shrink-0 text-blue-600" />
            {children}
        </p>
    );
}

function SuggestCard({ block }: { block: Extract<AskBlock, { kind: "suggest" }> }) {
    const [later, setLater] = useState(false);
    return (
        <div className={cn(ASK_CARD, "space-y-3")} data-ask-card="suggest">
            <CardHead label="Oura noticed" title="Start the page drafts" />
            <p className={cn(BODY, "m-0", INK)}>{block.text}</p>
            {later ? (
                <Confirm>Not now. It stays on Delivery until the drafts are started.</Confirm>
            ) : (
                <div className="flex flex-wrap items-center gap-2 pt-1">
                    <Link to={block.to} onClick={follow(block.to)} className={BTN_PRIMARY}>
                        Draft them in Page workshop
                    </Link>
                    <button type="button" onClick={() => setLater(true)} className={BTN_SECONDARY}>
                        Not now
                    </button>
                </div>
            )}
        </div>
    );
}

/** Start the right document: Oura's own Create a doc, with the template picked. A person presses Create the doc. */
function StartCard({ block }: { block: Extract<AskBlock, { kind: "start" }> }) {
    return (
        <div className={cn(ASK_CARD, "space-y-3")} data-ask-card="start">
            <div className="flex items-start gap-3">
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                    <FilePlus2Icon aria-hidden size={17} strokeWidth={1.75} />
                </span>
                <div className="min-w-0 flex-1">
                    <CardHead label="Create a doc · Client tab" title={`Start the ${block.docType}`} />
                    <p className={cn(BODY, "m-0 mt-1.5", INK)}>{block.text}</p>
                </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 pl-12">
                <Link to={block.to} onClick={follow(block.to)} className={BTN_PRIMARY} data-ask-start={block.docType}>
                    Open Create a doc
                </Link>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------------- *
 * Use case 1: the triage
 * ------------------------------------------------------------------------- */

const TRIAGE_SHOWN = 4;

/** What needs us, one card per group in order of urgency; each row opens its screen. */
function TriageBlock({ groups }: { groups: TriageGroup[] }) {
    return (
        <div className="space-y-3" data-ask-triage>
            {groups.map((g) => (
                <TriageCard key={g.key} group={g} />
            ))}
        </div>
    );
}

function TriageCard({ group }: { group: TriageGroup }) {
    const [all, setAll] = useState(false);
    const rows = all ? group.rows : group.rows.slice(0, TRIAGE_SHOWN);
    return (
        <section className={cn(ASK_FRAME, "overflow-hidden")} data-triage-group={group.key} data-triage-count={group.rows.length}>
            <div className="px-4 pb-2 pt-3.5">
                <CardHead label={group.hint} title={`${group.title} · ${group.count ?? group.rows.length}`} />
            </div>
            <ul className="list-plain m-0 p-0">
                {rows.map((r) => (
                    <li key={r.id} className="flex items-start gap-3 border-t border-black/[0.06] px-4 py-3" data-triage-row={r.id}>
                        <div className="min-w-0 flex-1">
                            <p className={cn(MICRO, "m-0 flex flex-wrap items-center gap-x-1.5", INK2)}>
                                <span className="font-medium text-[#374151]">{r.client}</span>
                                <span aria-hidden>·</span>
                                <span className={r.late ? LATE : undefined} data-triage-age>
                                    {r.age}
                                </span>
                            </p>
                            <p className={cn(BODY, "m-0 mt-0.5 break-words", INK)}>{r.text}</p>
                        </div>
                        <Link to={r.go.to} onClick={follow(r.go.to)} className={cn(BTN_SECONDARY, "mt-0.5 h-7 px-3 text-xs")} data-triage-go={r.go.label}>
                            {r.go.label}
                        </Link>
                    </li>
                ))}
            </ul>
            {group.rows.length > TRIAGE_SHOWN && (
                <div className="border-t border-black/[0.06] px-4 py-2.5">
                    <button type="button" onClick={() => setAll((v) => !v)} className={cn(META, "rounded font-medium text-blue-600 hover:text-blue-700", FOCUS_RING)} data-triage-more>
                        {all ? "Show fewer" : `Show ${group.rows.length - TRIAGE_SHOWN} more`}
                    </button>
                </div>
            )}
        </section>
    );
}

/* ------------------------------------------------------------------------- *
 * Use case 3: approved answers, the yes, what happens next
 * ------------------------------------------------------------------------- */

/** The approved answers as page changes: what the page says now, and what it would say. */
function ProposalsBlock({ origin, items }: { origin: string; items: ProposalPreview[] }) {
    return (
        <div className="space-y-3" data-ask-proposals={items.length}>
            {items.map((x, i) => (
                <section key={i} className={cn(ASK_CARD, "space-y-3")} data-ask-proposal>
                    <CardHead label={`${origin}, ${x.approved.split(", ").pop()}`} title={`Change ${i + 1} of ${items.length} · Homepage`} />
                    <dl className="m-0 space-y-2.5">
                        <div className="grid grid-cols-[52px_1fr] gap-3">
                            <dt className={cn(MICRO, "pt-0.5 font-medium", INK2)}>Now</dt>
                            <dd className={cn(BODY, "m-0 text-[#6B7280]")} data-proposal-before>
                                {x.before}
                            </dd>
                        </div>
                        <div className="grid grid-cols-[52px_1fr] gap-3">
                            <dt className={cn(MICRO, "pt-0.5 font-medium text-blue-600")}>New</dt>
                            <dd className={cn(BODY, "m-0 border-l-2 border-blue-600 pl-3 font-medium", INK)} data-proposal-after>
                                {x.after}
                            </dd>
                        </div>
                    </dl>
                </section>
            ))}
        </div>
    );
}

const STEP_DOT: Record<NextStep["state"], string> = {
    done: "bg-blue-600 text-white",
    now: "bg-white text-blue-600 ring-2 ring-inset ring-blue-600",
    preview: "bg-white text-gray-400 ring-1 ring-inset ring-dashed ring-gray-300 border border-dashed border-gray-300",
};

/**
 * After the yes: who said it, what a person does next, and what Oura does (a labelled preview here).
 * While this is the latest answer, the yes step plays Happy once in place of its tick, then rests on its smile.
 */
function NextBlock({ steps, note, celebrate }: { steps: NextStep[]; note: string; celebrate: boolean }) {
    const [result, setResult] = useState(false);
    return (
        <section className={cn(ASK_CARD, "space-y-4")} data-ask-next>
            <CardHead label="What happens next" title="From the yes to proof on the page" />
            <ol className="list-plain m-0 space-y-0 p-0">
                {steps.map((x, i) => {
                    const state = result && x.state === "preview" ? "done" : x.state;
                    return (
                        <li key={i} className="relative flex gap-3 pb-4 last:pb-0" data-next-step={x.state}>
                            {i < steps.length - 1 && <span aria-hidden className="absolute left-[11px] top-7 h-[calc(100%-24px)] w-px bg-black/[0.08]" />}
                            {celebrate && i === 0 && x.state === "done" ? (
                                // Happy bounces once on the yes, then rests on its smile. Its canvas is wider than the
                                // 24px dot, so it overhangs the dot's box instead of moving the step.
                                <span className="relative mt-0.5 h-6 w-6 shrink-0">
                                    <Mascot mood="happy" size={34} mode="once" className="absolute -left-[5px] -top-[5px]" />
                                </span>
                            ) : (
                                <span aria-hidden className={cn("relative mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-medium tabular-nums", STEP_DOT[state])}>
                                    {state === "done" ? <CheckIcon size={13} strokeWidth={2.5} /> : i + 1}
                                </span>
                            )}
                            <div className="min-w-0 flex-1">
                                <p className={cn(BODY, "m-0 flex flex-wrap items-center gap-2 font-medium", INK)}>
                                    {x.title}
                                    {x.state === "preview" && (
                                        <span className={cn(MICRO, "rounded-full bg-gray-100 px-2 py-0.5 font-medium", INK2)} data-next-preview>
                                            {result ? "Preview result" : "Preview"}
                                        </span>
                                    )}
                                    {x.state === "now" && <span className={cn(MICRO, "rounded-full bg-blue-600 px-2 py-0.5 font-medium text-white")}>Now</span>}
                                </p>
                                <p className={cn(META, "m-0 mt-0.5", INK2)}>{result && x.state === "preview" ? `${x.text} Seen on the page.` : x.text}</p>
                                {x.go && (
                                    <Link to={x.go.to} onClick={follow(x.go.to)} className={cn(META, "mt-1.5 inline-flex items-center gap-0.5 rounded font-medium text-blue-600 hover:text-blue-700", FOCUS_RING)} data-next-go={x.go.label}>
                                        {x.go.label}
                                        <ArrowUpRightIcon aria-hidden size={13} />
                                    </Link>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ol>
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-[#F9FAFB] px-3 py-2.5">
                <p className={cn(META, "m-0 min-w-0 flex-1", INK2)} data-next-note>
                    {note}
                </p>
                <button type="button" onClick={() => setResult((v) => !v)} className={cn(META, "shrink-0 rounded font-medium text-blue-600 hover:text-blue-700", FOCUS_RING)} data-next-toggle>
                    {result ? "Back to now" : "Show the preview result"}
                </button>
            </div>
        </section>
    );
}

/** Several screens, each with the one thing it shows. */
function LinksBlock({ links }: { links: { go: AskGo; text: string }[] }) {
    return (
        <ul className={cn(ASK_FRAME, "list-plain m-0 p-0")} data-ask-links>
            {links.map((l, i) => (
                <li key={l.go.to} className={cn("flex items-start gap-3 px-4 py-3", i > 0 && "border-t border-black/[0.06]")}>
                    <span aria-hidden className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-medium tabular-nums text-gray-600">
                        {i + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                        <Link to={l.go.to} onClick={follow(l.go.to)} className={cn(BODY, "inline-flex items-center gap-1 rounded font-medium text-blue-600 hover:text-blue-700", FOCUS_RING)} data-ask-go={l.go.label}>
                            {l.go.label}
                            <ArrowUpRightIcon aria-hidden size={14} />
                        </Link>
                        <p className={cn(META, "m-0 mt-0.5", INK2)}>{l.text}</p>
                    </div>
                </li>
            ))}
        </ul>
    );
}
